import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChevronDown } from 'lucide-react'
import type { RelacionadorMetricas } from '../../services/supervisaoVendas.service'

interface RelacionadoresTableProps {
  relacionadores: RelacionadorMetricas[]
  onRowClick: (relacionador: RelacionadorMetricas) => void
  isLoading?: boolean
}

type SortField = 'nome' | 'reunioesEste' | 'produzidoTotal' | 'produzidoVinculado'
type SortOrder = 'asc' | 'desc'

export function RelacionadoresTable({
  relacionadores,
  onRowClick,
  isLoading = false,
}: RelacionadoresTableProps) {
  const [sortField, setSortField] = useState<SortField>('reunioesEste')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }

  const sorted = [...relacionadores].sort((a, b) => {
    let aVal = a[sortField as keyof RelacionadorMetricas] as any
    let bVal = b[sortField as keyof RelacionadorMetricas] as any

    if (aVal === null) return 1
    if (bVal === null) return -1

    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
    return sortOrder === 'asc' ? cmp : -cmp
  })

  const getConfiancaBadge = (vinculados: number, total: number) => {
    if (total === 0) return <Badge variant="outline">—</Badge>
    const perc = Math.round((vinculados / total) * 100)
    if (perc === 100) return <Badge variant="default" className="bg-green-600">100%</Badge>
    if (perc >= 75) return <Badge variant="secondary">75%+</Badge>
    return <Badge variant="destructive">{perc}%</Badge>
  }

  const SortHeader = ({
    label,
    field,
  }: {
    label: string
    field: SortField
  }) => (
    <TableHead>
      <button
        onClick={() => toggleSort(field)}
        className="flex items-center gap-1 font-semibold hover:text-foreground"
      >
        {label}
        {sortField === field && (
          <ChevronDown className={`h-4 w-4 transition-transform ${
            sortOrder === 'asc' ? 'rotate-180' : ''
          }`} />
        )}
      </button>
    </TableHead>
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Carregando dados...</p>
      </div>
    )
  }

  if (relacionadores.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">Nenhum relacionador na equipe</p>
      </div>
    )
  }

  return (
    <div className="rounded-[16px] border border-[rgba(15,118,110,0.1)] overflow-hidden bg-white/50 dark:bg-zinc-950/40">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#f8fafc] dark:bg-zinc-900/60 hover:bg-[#f8fafc]">
            <SortHeader label="Relacionador" field="nome" />
            <SortHeader label="Reuniões" field="reunioesEste" />
            <SortHeader label="Produção" field="produzidoTotal" />
            <TableHead>Vinculados</TableHead>
            <TableHead>Vínculo %</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((relacionador) => (
            <TableRow
              key={relacionador.relacionadorId}
              className="hover:bg-muted/50 cursor-pointer"
              onClick={() => onRowClick(relacionador)}
            >
              <TableCell className="font-medium">{relacionador.nome}</TableCell>
              <TableCell className="text-center">
                <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                  {relacionador.reunioesEste}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-medium">
                  {relacionador.produzidoTotal}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <span className="text-sm">
                  {relacionador.produzidoVinculado}/{relacionador.produzidoTotal}
                </span>
              </TableCell>
              <TableCell>
                {getConfiancaBadge(relacionador.produzidoVinculado, relacionador.produzidoTotal)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    onRowClick(relacionador)
                  }}
                >
                  Detalhes
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
